import React, {
  useEffect,
  useRef,
  useState,
  useMemo,
  useCallback,
} from "react";
import { processedGeo$ } from "@a365/core";
import { useTranslation } from "react-i18next";
import Flags from "../flags";
import { Button, SupportButton } from "@a365/dsys";
import { mountRootParcel } from "single-spa";
import { navigateToUrl } from "single-spa";

const FooterComponent = () => {
  const { t, i18n } = useTranslation();
  const year = new Date().getFullYear();
  const [geo, setGeo] = useState(null);
  const [options, setOptions] = useState({});
  const [legalOptions, setLegalOptions] = useState(null);
  const geoUrl = location.pathname.split("/")[1] || "ar";

  // Parcel references

  const switchLanguageRef = useRef(null);
  const [switchLanguageParcel, setSwitchLanguageParcel] = useState(null);

  const socialNetworkRef = useRef(null);
  const [socialNetworkParcel, setSocialNetworkParcel] = useState(null);

  const [supportUIParcelMounted, setSupportUIParcelMounted] = useState(false);
  const supportUIContainerRef = useRef(null);

  const mountedSupportUIParcelRef = useRef(null);

  const isBR =
    geoUrl === "br" ||
    geoUrl === "pt" ||
    localStorage.getItem("lang") === "pt" ||
    i18n.language === "pt-BR";

  // Efecto para montar la parcela SwitchLanguage y SocialNetwork desde shared-ui
  useEffect(() => {
    let isMounted = true;
    const mountParcel = () => {
      if (switchLanguageRef.current && !switchLanguageParcel) {
        System.import("@a365/shared-ui")
          .then((parcelConfig) => {
            if (isMounted && switchLanguageRef.current) {
              const parcel = mountRootParcel(parcelConfig, {
                domElement: switchLanguageRef.current,
                show: "switch-language",
              });
              setSwitchLanguageParcel(parcel);
            }
          })
          .catch((err) => {
            console.error("Error al cargar shared-ui (SwitchLanguage):", err);
          });
      }
    };
    const timeoutId = setTimeout(mountParcel, 0);
    return () => {
      isMounted = false;
      clearTimeout(timeoutId);
      if (switchLanguageParcel) {
        switchLanguageParcel.unmount();
        setSwitchLanguageParcel(null);
      }
    };
  }, []);

  useEffect(() => {
    let isMounted = true;
    const mountParcel = () => {
      if (socialNetworkRef.current && !socialNetworkParcel) {
        System.import("@a365/shared-ui")
          .then((parcelConfig) => {
            if (isMounted && socialNetworkRef.current) {
              const parcel = mountRootParcel(parcelConfig, {
                domElement: socialNetworkRef.current,
                show: "social-network",
              });
              setSocialNetworkParcel(parcel);
            }
          })
          .catch((err) => {
            console.error("Error al cargar shared-ui (SocialNetwork):", err);
          });
      }
    };
    const timeoutId = setTimeout(mountParcel, 0);
    return () => {
      isMounted = false;
      clearTimeout(timeoutId);
      if (socialNetworkParcel) {
        socialNetworkParcel.unmount();
        setSocialNetworkParcel(null);
      }
    };
  }, []);

  // Función para montar la parcel support-ui usando single-spa
  const mountSupportUI = useCallback(
    async (view = "phone-support") => {
      // Si ya está montado, desmontarlo
      if (supportUIParcelMounted && mountedSupportUIParcelRef.current) {
        try {
          console.log("Desmontando parcela existente...");
          await mountedSupportUIParcelRef.current.unmount();
          console.log("Parcela desmontada correctamente");
          setSupportUIParcelMounted(false);
          mountedSupportUIParcelRef.current = null;
        } catch (error) {
          console.error("Error al desmontar la parcela:", error);
        }
        return;
      }

      // Verificar que la función mountRootParcel esté disponible
      if (!mountRootParcel) {
        console.error("mountRootParcel no está disponible");
        return;
      }

      const mountParcelLogic = async () => {
        // Verificar que el contenedor DOM existe
        if (!supportUIContainerRef.current) {
          console.error("El contenedor DOM para support-ui no está disponible");
          return;
        }

        try {
          // Si se llama desde el menú, hacer scroll al contenedor para asegurarse de que esté visible

          console.log("Importando módulo @a365/support-ui...");
          const parcelModule = await window.System.import("@a365/support-ui");
          console.log("Módulo importado correctamente");

          // Montar el parcel usando la API de single-spa
          const mountedParcel = mountRootParcel(
            parcelModule.default || parcelModule,
            {
              domElement: supportUIContainerRef.current,
              // Props que se pasarán al componente - Ahora usamos el parámetro view
              view,
              // Pasamos onCancel como función que desmonta la parcela
              onCancel: async (result) => {
                console.log(
                  "Support UI operation finished or cancelled:",
                  result
                );

                // Desmontar la parcela cuando el modal se cierra o una operación tiene éxito
                if (mountedSupportUIParcelRef.current) {
                  try {
                    await mountedSupportUIParcelRef.current.unmount();
                    setSupportUIParcelMounted(false);
                    mountedSupportUIParcelRef.current = null;
                    console.log(
                      "Support UI unmounted due to modal close or operation success"
                    );
                  } catch (err) {
                    console.error("Error al desmontar en onCancel:", err);
                  }
                }
              },
              setLayoutMode: (mode) => {
                console.log("Layout mode changed:", mode);
              },
              supportController: { fetchData: async () => ({ status: true }) },
              // Aumentar el timeout para evitar el error #31
              timeoutMs: 10000, // Aumentar a 10 segundos (el default es 3000ms)
            }
          );

          // Guardar referencia al parcel montado para poder desmontarlo después
          mountedSupportUIParcelRef.current = mountedParcel;
          setSupportUIParcelMounted(true);

          console.log("Support UI montado correctamente en Footer");

          // Esperar a que el montaje se complete totalmente
          try {
            await mountedParcel.mountPromise;
            console.log(
              "Montaje de parcela completado (mountPromise resuelto)"
            );
          } catch (mountError) {
            console.error("Error durante la promesa de montaje:", mountError);
            // No rethrow, seguir con el flujo normal
          }
        } catch (err) {
          console.error("Error al montar Support UI en Footer:", err);
          // Limpiar estado en caso de error
          setSupportUIParcelMounted(false);
          if (mountedSupportUIParcelRef.current) {
            try {
              await mountedSupportUIParcelRef.current.unmount();
            } catch (unmountErr) {
              // Ignorar errores secundarios durante la limpieza
            }
            mountedSupportUIParcelRef.current = null;
          }
        }
      };
      await mountParcelLogic();
    },
    [supportUIParcelMounted]
  );

  // Cleanup effect para la parcela support-ui
  useEffect(() => {
    return () => {
      if (mountedSupportUIParcelRef.current) {
        mountedSupportUIParcelRef.current
          .unmount()
          .then(() => {
            console.log("Support UI unmounted on FooterComponent cleanup");
            mountedSupportUIParcelRef.current = null;
          })
          .catch((err) => {
            console.error(
              "Error unmounting Support UI on FooterComponent cleanup:",
              err
            );
          });
      }
    };
  }, []);

  const baseOptions = useMemo(() => {
    return {
      1: [
        { label: t("options.contact"), type: "title" },
        { label: t("options.sales"), value: "whatsapp", type: "link" },
        {
          label: t("options.needAssistance"),
          value: "get-assistance",
          type: "modal",
        },
        {
          label: t("options.claims"),
          value: "claims",
          type: "external_link",
        },
      ],
      2: [
        { label: t("options.travelers"), type: "title" },
        { label: t("options.myVoucher"), value: `mi-voucher`, type: "link" },
        {
          label: t("options.refunds"),
          value: `reintegros-express`,
          type: "link",
        },
        {
          label: t("options.faqs"),
          value: `preguntas-frecuentes`,
          type: "link",
        },
        {
          label: t("options.ccgg"),
          value: `ccgg`,
          type: "link",
        },
      ],
      3: [
        { label: t("options.utils"), type: "title" },
        { label: t("options.affiliates"), value: `afiliados`, type: "link" },
        { label: t("options.enterprises"), value: "empresas", type: "link" },
      ],
    };
  }, [t, i18n.language]);

  const optionsLegal = useMemo(() => {
    return [
      {
        label: t("options.policyPrivacy"),
        value: `${
          isBR
            ? "https://assistcdn.s3.us-west-1.amazonaws.com/assets/site/br/files/Politica_de_Privacidade.pdf"
            : "https://app.assist-365.com/upload/politica_de_privacidad.pdf"
        }`,
        type: "download",
        target: "_blank",
      },
      {
        label: t("options.policyCookies"),
        value: `${
          isBR
            ? "https://assistcdn.s3.us-west-1.amazonaws.com/assets/site/br/files/Politica_de_Cookies.pdf"
            : "https://app.assist-365.com/upload/politica_de_cookies.pdf"
        }`,
        type: "download",
        target: "_blank",
      },
      {
        label: t("options.regretButton"),
        value: "cancellation-service",
        type: "link",
        target: "_blank",
      },
      {
        label: t("options.unsubscribeService"),
        value: "cancellation-service",
        type: "link",
        target: "_blank",
      },
      {
        label: t("options.unsubscribeButton"),
        value: "cancel-subscription",
        type: "link",
        target: "_blank",
      },
    ];
  }, [t, isBR, i18n.language]);

  useEffect(() => {
    const subscription = processedGeo$.subscribe((data) => {
      setGeo(data);
    });

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  useEffect(() => {
    if (geo && Object.keys(geo).length > 0) {
      const updatedOptions = JSON.parse(JSON.stringify(baseOptions));

      if (["ar", "br", "AR", "BR"].includes(geoUrl.toLowerCase())) {
        updatedOptions[3].push({
          label: t("options.blog"),
          value: `https://assist-365.com/blog/${geoUrl.toLowerCase()}`,
          type: "external_link",
          target: "_blank",
        });
      }

      if (geo?.country === "AR" && localStorage.getItem("lang") !== "pt") {
        const phoneSupportOption = {
          label: t("options.phoneSupport"),
          value: "phone-support",
          type: "modal",
        };

        const getAssistanceOption = updatedOptions[1].find(
          (opt) => opt.value === "get-assistance"
        );

        const claimsOption = {
          label: t("options.claims"),
          value: "claims",
          type: "external_link",
        };

        const remaining = updatedOptions[1].filter(
          (opt) =>
            !["phone-support", "get-assistance", "claims"].includes(opt.value)
        );

        updatedOptions[1] = [
          ...remaining,
          phoneSupportOption,
          ...(getAssistanceOption ? [getAssistanceOption] : []),
          claimsOption,
        ];
      }

      let updatedLegalOptions = [...optionsLegal];
      if (
        geo?.country === "BR" ||
        geo?.country === "PT" ||
        localStorage.getItem("lang") === "pt" ||
        i18n.language === "pt-BR"
      ) {
        const regretIndex = updatedLegalOptions.findIndex(
          (option) => option.label === t("options.regretButton")
        );
        if (regretIndex !== -1) {
          updatedLegalOptions.splice(regretIndex, 1);
        }

        const unsubscribeIndex = updatedLegalOptions.findIndex(
          (option) => option.label === t("options.unsubscribeService")
        );
        if (unsubscribeIndex !== -1) {
          updatedLegalOptions.splice(unsubscribeIndex, 1);
        }
      }

      setLegalOptions(updatedLegalOptions);
      setOptions(updatedOptions);
    }
  }, [geo, baseOptions, optionsLegal, t, i18n.language]);

  const optionsMap = new Map(Object.entries(options));

  const claimLink = isBR
    ? "https://assist-365-3342240.hs-sites.com/reclama%C3%A7%C3%B5es.assist365"
    : i18n.language === "es-AR" || geoUrl === "ar"
    ? "https://assist-365-3342240.hs-sites.com/contacto.assist365"
    : "https://assist-365-3342240.hs-sites.com/contacto.assist365";

  const handleOption = useCallback(
    (opt) => {
      const { type, value, target } = opt;

      if (type === "link") {
        if (target === "_blank") {
          const url = `https://assist-365.com/${geoUrl}/${value}`;
          window.open(url, "_blank");
        } else {
          scrollTo(0, 0); // Scroll to top
          const href = `/${geoUrl}/${value}`;
          requestAnimationFrame(() => {
            window.scrollTo({ top: 0, behavior: "auto" });
          });
          navigateToUrl(href);
        }
        return;
      }

      if (type === "external_link") {
        if (value === "claims") {
          window.open(claimLink, "_blank");
        } else {
          window.open(value, "_blank");
        }
        return;
      }

      if (type === "download") {
        window.open(value, "blank");
        return;
      }

      if (type === "modal") {
        if (value === "contact") {
          mountSupportUI("phone-support");
        } else if (value === "get-assistance") {
          mountSupportUI("get-assistance");
        }
        return;
      }
    },
    [geoUrl, mountSupportUI]
  );

  return (
    <>
      <div id="support-ui-container-footer" ref={supportUIContainerRef}></div>
      <div className="w-full px-4 md:px-0">
        <div className="max-w-[358px] sm:max-w-[548px] md:w-full xl:max-w-[1200px] pb-8 lg:pb-16 flex flex-col-reverse xl:flex-row xl:justify-between items-start w-full mx-auto py-10">
          <div className="flex flex-col mx-auto lg:mx-0 w-full mt-6 lg:mt-0 p-4 px-0 lg:px-0 lg:p-0">
            <div
              id="social-network-container"
              ref={socialNetworkRef}
              className="social-network-container"
            ></div>
            <div
              ref={switchLanguageRef}
              className="mt-6 switch-language-container"
            ></div>
          </div>

          <div className=" lg:pb-[26px] flex items-center justify-start w-full max-w-[588px]">
            <div className="w-full sm:grid sm:grid-cols-3 xlg:gap-x-[64px] gap-y-6 gap-x-4">
              {Array.from(optionsMap.entries()).map(([group, opts]) => (
                <ul
                  className="flex flex-col items-start justify-start text-base text-text-decorative-darker font-semibold _mt-6"
                  key={group}
                >
                  {opts.map((opt, index) => {
                    if (opt.type === "modal") {
                      return (
                        <li className="cursor-pointer" key={index}>
                          <Button
                            variant="default"
                            color="tertiary"
                            onClick={() => mountSupportUI(opt.value)}
                            size="small"
                            padding={false}
                          >
                            {opt.label}
                          </Button>
                        </li>
                      );
                    }

                    if (opt.value === "whatsapp") {
                      return (
                        <li className="cursor-pointer" key={index}>
                          <SupportButton
                            size="small"
                            fullWidth
                            variant="default"
                            color="tertiary"
                            align="left"
                            padding={false}
                            label={`${opt?.label}`}
                          ></SupportButton>
                        </li>
                      );
                    }

                    return (
                      <li onClick={() => handleOption(opt)} key={index}>
                        {opt.type === "title" ? (
                          <div className="mb-2 text-text-decorative-darker font-semibold text-base block">
                            {opt.label}
                          </div>
                        ) : (
                          <Button
                            size="small"
                            fullWidth
                            variant="default"
                            color="tertiary"
                            padding={false}
                            align="left"
                          >
                            {opt.label}
                          </Button>
                        )}
                      </li>
                    );
                  })}
                </ul>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="bg-bg-alt-secondary px-4 pt-10 pb-16 w-full">
        <div className="w-full max-w-[358px] sm:max-w-[548px] xl:max-w-[1200px] mx-auto">
          {legalOptions && (
            <ul className="sm:leading-10 text-base text-text-alt-decorative flex sm:items-center justify-start items-center font-semibold gap-y-4 flex-wrap border-b-[1px] border-border-alt-secondary pb-6">
              {legalOptions.map((v, index) => (
                <li
                  className="mr-4 cursor-pointer text-text-alt-decorative"
                  key={index}
                >
                  <Button
                    size="small"
                    variant="alt"
                    color="tertiary"
                    onClick={() => handleOption(v)}
                  >
                    {v.label}
                  </Button>
                </li>
              ))}
            </ul>
          )}
          <p className="mt-6 text-sm text-white">
            {t("copyright", { year: year })}
            <br />
            <span className="py-4 block">{t("dropline")}</span>
          </p>
          <p className="mt-0 text-sm text-white leading-5">{t("legal")}</p>
          <Flags />
        </div>
      </div>
    </>
  );
};

export default FooterComponent;
